// File path: src/utils/jobStorage.js
// Purpose: Handles saving, loading, updating, deleting, searching, and opening saved ManuCalc manufacturing plans.

import { MATERIALS } from "../data/manufacturingKnowledge";
import {
  buildManufacturingOperationsDashboard,
  calculateManufacturingReadiness,
  calculateOperationProgress,
  getPlanBomItems,
  getPlanMaterialRequirements,
  normalizeManufacturingStatus,
} from "./manufacturingOperations";
import { normalizeJobForRiskEngine } from "./jobRiskEngine";
import { queueCloudJobDelete, queueCloudJobSave } from "./workspaceCloudSync";
import { syncLocalPreference } from "./userPreferenceCloudStore";

const STORAGE_KEY = "manucalc_jobs_v2";
const LEGACY_STORAGE_KEY = "manucalc_jobs_v1";
const RECENT_JOBS_KEY = "manucalc_recent_jobs_v1";
const LAST_OPEN_JOB_KEY = "manucalc_last_open_job_v1";
const CURRENT_SCHEMA_VERSION = 2;

const nowIso = () => new Date().toISOString();

const createId = (prefix = "id") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const ensureNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const defaultDimensions = (fallback = {}) => ({
  length: ensureNumber(fallback.length, 1),
  width: ensureNumber(fallback.width, 1),
  height: ensureNumber(fallback.height, 1),
});

const defaultStock = (fallback = {}) => ({
  length: ensureNumber(fallback.length, 1.25),
  width: ensureNumber(fallback.width, 1.25),
  height: ensureNumber(fallback.height, 1.25),
});

const normalizeFile = (file = {}) => {
  const id = file.id || file.fileId || createId("file");
  const name = file.name || file.fileName || file.originalName || "Untitled File";
  const size = ensureNumber(file.size ?? file.fileSize, 0);
  const mimeType = file.mimeType || file.type || "unknown";
  const extension = file.extension || String(name).split(".").pop()?.toLowerCase() || "";
  const downloadUrl = file.downloadUrl || file.fileUrl || "";
  return {
    ...file,
    id,
    fileId: id,
    name,
    fileName: name,
    originalName: file.originalName || name,
    size,
    fileSize: size,
    type: mimeType,
    mimeType,
    extension,
    fileType: file.fileType || extension.toUpperCase() || "File",
    category: file.category || "Attachment",
    uploadedAt: file.uploadedAt || nowIso(),
    uploadedBy: file.uploadedBy || "",
    uploadedByName: file.uploadedByName || "",
    previewUrl: file.previewUrl || downloadUrl || file.fileDataUrl || file.dataUrl || "",
    previewType: file.previewType || "",
    dataUrl: file.dataUrl || "",
    fileDataUrl: file.fileDataUrl || "",
    fileUrl: downloadUrl,
    downloadUrl,
    storagePath: file.storagePath || "",
    uploadStatus: file.uploadStatus || (file.storagePath ? "uploaded" : file.fileDataUrl || file.dataUrl ? "local-fallback" : ""),
    uploadError: file.uploadError || "",
    notes: file.notes || "",
  };
};

const normalizePart = (part = {}, job = {}, index = 0) => ({
  id: part.id || createId("part"),
  name: part.name || (index === 0 ? job.name || "Primary Part" : `Part ${index + 1}`),
  partNumber: part.partNumber || (index === 0 ? job.partNumber || "" : ""),
  quantity: Math.max(1, ensureNumber(part.quantity, index === 0 ? job.quantity || 1 : 1)),
  materialId: part.materialId || job.materialId || MATERIALS[0]?.id || "aluminum-6061",
  dimensions: defaultDimensions(part.dimensions || (index === 0 ? job.dimensions : {})),
  stock: defaultStock(part.stock || (index === 0 ? job.stock : {})),
  files: ensureArray(part.files).map(normalizeFile),
  notes: part.notes || "",
});

const normalizeOperation = (operation = {}, index = 0) => ({
  id: operation.id || createId("op"),
  name: operation.name || `Operation ${index + 1}`,
  operationTypeId: operation.operationTypeId || operation.operationType || "facing",
  machineId: operation.machineId || operation.machine || "cnc-mill-3-axis",
  setupNumber: Math.max(1, ensureNumber(operation.setupNumber, index + 1)),
  workPerformed: operation.workPerformed || "",
  toolDescription: operation.toolDescription || "",
  toolsUsed: operation.toolsUsed || "",
  inspectionCriteria: operation.inspectionCriteria || "",
  images: ensureArray(operation.images).map(normalizeFile),
  toolMaterial: operation.toolMaterial || "Carbide",
  toolDiameter: ensureNumber(operation.toolDiameter, 0.5),
  stockDiameter: ensureNumber(operation.stockDiameter || operation.stockDiameterX, 1),
  fluteCount: Math.max(1, ensureNumber(operation.fluteCount, 4)),
  depthOfCut: ensureNumber(operation.depthOfCut, 0.05),
  widthOfCut: ensureNumber(operation.widthOfCut, 0.1),
  setupHours: ensureNumber(operation.setupHours, 0.5),
  runHours: ensureNumber(operation.runHours, 0.5),
  machineHourlyRate: operation.machineHourlyRate === "" ? "" : ensureNumber(operation.machineHourlyRate, 0),
  laborHourlyRate: operation.laborHourlyRate === "" ? "" : ensureNumber(operation.laborHourlyRate, 0),
  machineRateIncludesOperator: operation.machineRateIncludesOperator === "yes" ? "yes" : "no",
  coolant: operation.coolant || "Flood coolant",
  notes: operation.notes || "",
  riskNotes: ensureArray(operation.riskNotes),
});

const normalizeProcess = (process = {}, job = {}) => ({
  name: process.name || job.name || "Manufacturing Process",
  owner: process.owner || "",
  revision: process.revision || job.revision || "A",
  objective:
    process.objective ||
    `Manufacture ${job.name || "this job"} to the current customer requirements.`,
});

const normalizeCalculation = (calculation = {}, index = 0) => ({
  ...calculation,
  id: calculation.id || createId("calc"),
  toolId: calculation.toolId || calculation.calculatorId || "",
  toolName:
    calculation.toolName ||
    calculation.calculatorName ||
    calculation.toolId ||
    `Calculation ${index + 1}`,
  summary: calculation.summary || calculation.title || "Saved calculation",
  data:
    calculation.data ||
    calculation.rawData ||
    calculation.snapshot ||
    calculation.calculationSnapshot ||
    null,
  rawData:
    calculation.rawData ||
    calculation.data ||
    calculation.snapshot ||
    calculation.calculationSnapshot ||
    null,
  calculationSnapshot:
    calculation.calculationSnapshot ||
    calculation.snapshot ||
    calculation.rawData ||
    calculation.data ||
    null,
  inputs: calculation.inputs || calculation.data?.inputs || calculation.rawData?.inputs || null,
  outputs:
    calculation.outputs ||
    calculation.results ||
    calculation.data?.outputs ||
    calculation.data?.results ||
    calculation.rawData?.outputs ||
    calculation.rawData?.results ||
    null,
  formulas:
    calculation.formulas ||
    calculation.formula ||
    calculation.data?.formulas ||
    calculation.data?.formula ||
    calculation.rawData?.formulas ||
    calculation.rawData?.formula ||
    null,
  assumptions:
    calculation.assumptions ||
    calculation.data?.assumptions ||
    calculation.rawData?.assumptions ||
    null,
  sources:
    calculation.sources ||
    calculation.references ||
    calculation.data?.sources ||
    calculation.data?.references ||
    calculation.rawData?.sources ||
    calculation.rawData?.references ||
    null,
  units:
    calculation.units ||
    calculation.unit ||
    calculation.unitSystem ||
    calculation.data?.units ||
    calculation.data?.unit ||
    calculation.data?.unitSystem ||
    calculation.rawData?.units ||
    calculation.rawData?.unit ||
    calculation.rawData?.unitSystem ||
    null,
  partId: calculation.partId || "",
  partName: calculation.partName || "",
  customer: calculation.customer || "",
  createdAt: calculation.createdAt || nowIso(),
  updatedAt: calculation.updatedAt || calculation.createdAt || nowIso(),
});

const normalizeActivityEvent = (event = {}, index = 0) => ({
  id: event.id || createId("activity"),
  type: event.type || event.eventType || "plan_updated",
  title: event.title || event.summary || `Activity ${index + 1}`,
  detail: event.detail || event.description || "",
  actorId: event.actorId || event.userId || "",
  actorName: event.actorName || event.userName || "",
  createdAt: event.createdAt || event.timestamp || nowIso(),
});

const normalizeRevisionRecord = (revision = {}, index = 0) => ({
  id: revision.id || createId("revision"),
  revision: revision.revision || revision.identifier || `Rev ${index + 1}`,
  date: revision.date || revision.revisionDate || nowIso(),
  note: revision.note || revision.notes || "",
  author: revision.author || revision.authorName || "",
  changeSummary: revision.changeSummary || revision.summary || "",
});

const normalizeToolPurchaseItem = (item = {}) => {
  const quantity = ensureNumber(item.quantity, 1);
  const unitCost = ensureNumber(item.unitCost, 0);
  return {
    id: item.id || createId("tool-purchase"),
    toolId: item.toolId || "",
    toolName: item.toolName || item.name || "",
    description: item.description || "",
    quantity,
    unitCost,
    extendedCost: ensureNumber(item.extendedCost, quantity * unitCost),
    supplier: item.supplier || item.source || "",
    notes: item.notes || "",
  };
};

const normalizeCosting = (costing = {}) => ({
  materialCost: costing.materialCost === "" ? "" : ensureNumber(costing.materialCost, 0),
  defaultLaborRate: costing.defaultLaborRate === "" ? "" : ensureNumber(costing.defaultLaborRate, 75),
  laborHours: costing.laborHours === "" ? "" : ensureNumber(costing.laborHours, 0),
  designHours: costing.designHours === "" ? "" : ensureNumber(costing.designHours, 0),
  designHourlyRate: costing.designHourlyRate === "" ? "" : ensureNumber(costing.designHourlyRate, 0),
  designCost: costing.designCost === "" ? "" : ensureNumber(costing.designCost, 0),
  engineeringHours: costing.engineeringHours === "" ? "" : ensureNumber(costing.engineeringHours, 0),
  engineeringRate: costing.engineeringRate === "" ? "" : ensureNumber(costing.engineeringRate, 0),
  cadCamProgrammingHours: costing.cadCamProgrammingHours === "" ? "" : ensureNumber(costing.cadCamProgrammingHours, 0),
  cadCamProgrammingRate: costing.cadCamProgrammingRate === "" ? "" : ensureNumber(costing.cadCamProgrammingRate, 0),
  designNotes: costing.designNotes || "",
  toolingCost: costing.toolingCost === "" ? "" : ensureNumber(costing.toolingCost, 0),
  outsideProcessingCost: costing.outsideProcessingCost === "" ? "" : ensureNumber(costing.outsideProcessingCost, 0),
  shippingCost: costing.shippingCost === "" ? "" : ensureNumber(costing.shippingCost, 0),
  additionalCost: costing.additionalCost === "" ? "" : ensureNumber(costing.additionalCost, 0),
  overheadRate: costing.overheadRate === "" ? "" : ensureNumber(costing.overheadRate, 0),
  profitMarginRate: costing.profitMarginRate === "" ? "" : ensureNumber(costing.profitMarginRate, 0),
  toolPurchaseItems: ensureArray(costing.toolPurchaseItems).map(normalizeToolPurchaseItem),
});

export const normalizeStoredJob = (job = {}) => {
  const riskNormalized = normalizeJobForRiskEngine(job || {});
  const baseJob = {
    ...job,
    ...riskNormalized,
  };

  const fallbackPart = {
    id: "primary-part",
    name: baseJob.name || "Primary Part",
    partNumber: baseJob.partNumber || "",
    quantity: baseJob.quantity || 1,
    materialId: baseJob.materialId || MATERIALS[0]?.id || "aluminum-6061",
    dimensions: baseJob.dimensions,
    stock: baseJob.stock,
    files: ensureArray(baseJob.files),
    notes: "",
  };

  const partsSource = ensureArray(baseJob.parts).length ? baseJob.parts : [fallbackPart];
  const normalizedParts = partsSource.map((part, index) => normalizePart(part, baseJob, index));
  const primaryPart = normalizedParts[0];
  const normalizedOperations = ensureArray(baseJob.operations).map(normalizeOperation);
  const normalizedForDerivedData = {
    ...baseJob,
    parts: normalizedParts,
    operations: normalizedOperations,
  };
  const bom = getPlanBomItems(normalizedForDerivedData);
  const materialRequirements = getPlanMaterialRequirements({
    ...normalizedForDerivedData,
    bom,
  });
  const operationProgress = calculateOperationProgress(normalizedOperations);
  const readiness = calculateManufacturingReadiness({
    ...normalizedForDerivedData,
    bom,
  });
  const status = normalizeManufacturingStatus(
    baseJob.status || baseJob.productionStatus
  );

  return {
    ...baseJob,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: baseJob.id || createId("job"),
    name: baseJob.name || "Untitled Manufacturing Job",
    customer: baseJob.customer || "",
    partNumber: baseJob.partNumber || primaryPart.partNumber || "",
    revision: baseJob.revision || "",
    quantity: Math.max(1, ensureNumber(baseJob.quantity, primaryPart.quantity || 1)),
    status,
    priority: baseJob.priority || "Normal",
    dueDate: baseJob.dueDate || baseJob.requiredDate || "",
    assignedEmployeeIds: ensureArray(
      baseJob.assignedEmployeeIds || baseJob.assignedEmployees
    ),
    materialId: baseJob.materialId || primaryPart.materialId || MATERIALS[0]?.id || "aluminum-6061",
    dimensions: defaultDimensions(baseJob.dimensions || primaryPart.dimensions),
    stock: defaultStock(baseJob.stock || primaryPart.stock),
    parts: normalizedParts,
    files: ensureArray(baseJob.files).map(normalizeFile),
    operations: normalizedOperations,
    selectedMachines: ensureArray(baseJob.selectedMachines),
    selectedToolIds: ensureArray(baseJob.selectedToolIds),
    suggestedRoute: baseJob.suggestedRoute || null,
    acceptedRoute: ensureArray(baseJob.acceptedRoute),
    cutList: baseJob.cutList || null,
    process: normalizeProcess(baseJob.process, baseJob),
    calculations: ensureArray(baseJob.calculations).map(normalizeCalculation),
    bom,
    materialRequirements,
    readiness,
    operationProgress,
    productionState: {
      status,
      currentOperationId: baseJob.productionState?.currentOperationId || "",
      lastUpdatedAt:
        baseJob.productionState?.lastUpdatedAt || baseJob.updatedAt || nowIso(),
    },
    qualityRequirements: ensureArray(baseJob.qualityRequirements),
    revisionHistory: ensureArray(baseJob.revisionHistory).map(
      normalizeRevisionRecord
    ),
    activityHistory: ensureArray(baseJob.activityHistory).map(
      normalizeActivityEvent
    ),
    costing: normalizeCosting(baseJob.costing),
    notes: baseJob.notes || "",
    createdAt: baseJob.createdAt || nowIso(),
    updatedAt: baseJob.updatedAt || nowIso(),
  };
};

const readRawJobs = () => {
  const currentRaw = localStorage.getItem(STORAGE_KEY);
  if (currentRaw) return safeParse(currentRaw, []);

  const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacyRaw) return safeParse(legacyRaw, []);

  return [];
};

const writeJobs = (jobs) => {
  const normalizedJobs = ensureArray(jobs).map(normalizeStoredJob);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedJobs));
  return normalizedJobs;
};

const writeRecentJobIds = (jobIds) => {
  const nextRecent = ensureArray(jobIds).slice(0, 8);
  localStorage.setItem(RECENT_JOBS_KEY, JSON.stringify(nextRecent));
  syncLocalPreference(RECENT_JOBS_KEY, nextRecent);
  return nextRecent;
};

const writeLastOpenJobId = (jobId) => {
  const nextJobId = jobId || "";
  localStorage.setItem(LAST_OPEN_JOB_KEY, nextJobId);
  syncLocalPreference(LAST_OPEN_JOB_KEY, nextJobId);
  return nextJobId;
};

export const mergeCloudJobs = (jobs) => {
  const normalizedJobs = writeJobs(jobs);
  window.dispatchEvent(new CustomEvent("manucalc:jobs-updated"));
  return normalizedJobs;
};

export const getAllJobs = () => {
  const jobs = readRawJobs();
  const normalizedJobs = ensureArray(jobs).map(normalizeStoredJob);

  if (JSON.stringify(jobs) !== JSON.stringify(normalizedJobs)) {
    writeJobs(normalizedJobs);
  }

  return normalizedJobs.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
};

export const getJobById = (jobId) => {
  if (!jobId) return null;
  const jobs = getAllJobs();
  return jobs.find((job) => job.id === jobId) || null;
};

export const saveJob = (job) => {
  const normalizedJob = normalizeStoredJob({
    ...job,
    updatedAt: nowIso(),
  });

  const jobs = getAllJobs();
  const existingIndex = jobs.findIndex((j) => j.id === normalizedJob.id);

  if (existingIndex !== -1) {
    jobs[existingIndex] = {
      ...jobs[existingIndex],
      ...normalizedJob,
      createdAt: jobs[existingIndex].createdAt || normalizedJob.createdAt,
      updatedAt: nowIso(),
    };
  } else {
    jobs.push({
      ...normalizedJob,
      createdAt: normalizedJob.createdAt || nowIso(),
      updatedAt: nowIso(),
    });
  }

  writeJobs(jobs);
  markJobOpened(normalizedJob.id);
  queueCloudJobSave(normalizedJob);
  window.dispatchEvent(new CustomEvent("manucalc:jobs-updated"));
  return normalizedJob;
};

export const patchJob = (jobId, updates = {}) => {
  const existingJob = getJobById(jobId);
  if (!existingJob) return null;
  return saveJob({ ...existingJob, ...updates });
};

export const deleteJob = (jobId) => {
  const jobs = getAllJobs().filter((job) => job.id !== jobId);
  writeJobs(jobs);

  const recent = getRecentJobIds().filter((id) => id !== jobId);
  writeRecentJobIds(recent);

  if (localStorage.getItem(LAST_OPEN_JOB_KEY) === jobId) {
    writeLastOpenJobId("");
  }
  queueCloudJobDelete(jobId);
  window.dispatchEvent(new CustomEvent("manucalc:jobs-updated"));
};

export const duplicateJob = (jobId) => {
  const job = getJobById(jobId);
  if (!job) return null;

  const duplicatedParts = ensureArray(job.parts).map((part, index) => ({
    ...part,
    id: createId("part"),
    name: index === 0 ? `${part.name || "Primary Part"} (Copy)` : part.name,
    files: ensureArray(part.files).map((file) => ({ ...file, id: createId("file") })),
  }));

  const duplicatedOperations = ensureArray(job.operations).map((operation) => ({
    ...operation,
    id: createId("op"),
  }));

  const newJob = normalizeStoredJob({
    ...job,
    id: createId("job"),
    name: `${job.name || "Untitled Job"} (Copy)`,
    parts: duplicatedParts,
    operations: duplicatedOperations,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });

  saveJob(newJob);
  return newJob;
};

export const clearAllJobs = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  writeRecentJobIds([]);
  writeLastOpenJobId("");
};

export const searchJobs = (query) => {
  const jobs = getAllJobs();
  const q = String(query || "").trim().toLowerCase();

  if (!q) return jobs;

  return jobs.filter((job) => {
    const searchableText = [
      job.name,
      job.partNumber,
      job.customer,
      job.revision,
      job.materialId,
      ...(job.machineRoute || []),
      ...(job.parts || []).flatMap((part) => [
        part.name,
        part.partNumber,
        part.materialId,
        ...(part.files || []).map((file) => file.name),
      ]),
      ...(job.operations || []).map((operation) => operation.name || operation.operationTypeId || ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(q);
  });
};

export const markJobOpened = (jobId) => {
  if (!jobId) return;

  writeLastOpenJobId(jobId);

  const recent = getRecentJobIds().filter((id) => id !== jobId);
  writeRecentJobIds([jobId, ...recent]);
};

export const getRecentJobIds = () => {
  const raw = localStorage.getItem(RECENT_JOBS_KEY);
  return ensureArray(raw ? safeParse(raw, []) : []);
};

export const getRecentJobs = () => {
  const jobs = getAllJobs();
  const recentIds = getRecentJobIds();

  return recentIds
    .map((id) => jobs.find((job) => job.id === id))
    .filter(Boolean);
};

export const getLastOpenJob = () => {
  const jobId = localStorage.getItem(LAST_OPEN_JOB_KEY);
  return jobId ? getJobById(jobId) : null;
};

export const getJobStats = () => {
  const jobs = getAllJobs();
  const operationsDashboard = buildManufacturingOperationsDashboard(jobs);

  return {
    totalJobs: jobs.length,
    totalParts: jobs.reduce((sum, job) => sum + ensureArray(job.parts).length, 0),
    totalFiles: jobs.reduce(
      (sum, job) =>
        sum +
        ensureArray(job.parts).reduce(
          (partSum, part) => partSum + ensureArray(part.files).length,
          0
        ),
      0
    ),
    totalOperations: jobs.reduce((sum, job) => sum + ensureArray(job.operations).length, 0),
    operationsDashboard,
    recentlyOpened: getRecentJobs().length,
    lastOpenJob: getLastOpenJob(),
  };
};

export const getManufacturingOperationsDashboard = () =>
  buildManufacturingOperationsDashboard(getAllJobs());
